import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { platformLayoutRoute } from './platformLayout.js';
import { PlatformModal } from '../../components/platform/PlatformModal.js';
import { PlatformApiError } from '../../lib/platform-auth.js';
import {
  listPaddleMappings,
  validatePaddleMapping,
  upsertPaddleMapping,
  type PaddleMapping,
  type PaddlePriceView,
  type SlotValidation,
} from '../../api/platform.js';
import type { SubscriptionTier } from '../../api/subscriptions.js';

/** Only starter + growth have catalogue entries; enterprise is bespoke. */
const TIERS: { tier: SubscriptionTier; label: string }[] = [
  { tier: 'starter', label: 'Starter' },
  { tier: 'growth', label: 'Growth' },
];

/** Paddle amounts are minor units; our catalogue is USD (2-decimal). */
function formatAmount(view: PaddlePriceView): string {
  const major = (Number(view.amount) / 100).toFixed(2);
  const per = view.interval ? `/${view.interval}` : ' one-time';
  return `${view.currencyCode} ${major}${per}`;
}

function PaddleMappingsPage(): JSX.Element {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['platform', 'paddle-mappings'],
    queryFn: listPaddleMappings,
  });
  const [editing, setEditing] = useState<SubscriptionTier | null>(null);

  const byTier = new Map<string, PaddleMapping>((data?.mappings ?? []).map((m) => [m.tier, m]));

  return (
    <div className="p-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-indigo-deep">Paddle Catalogue Mappings</h1>
          <p className="text-sm text-gray-500">
            Tier → Paddle product &amp; price ids. Checkout resolves a tier to a price id through these rows —
            each id is validated against Paddle before it can be saved.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded border border-indigo-deep px-3 py-1.5 text-sm text-indigo-deep hover:bg-indigo-deep hover:text-white"
        >
          Refresh
        </button>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-600" role="alert">
          Could not load mappings: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-3 py-2">Tier</th>
                <th className="px-3 py-2">Product ID</th>
                <th className="px-3 py-2">Monthly price ID</th>
                <th className="px-3 py-2">Annual price ID</th>
                <th className="px-3 py-2">Updated</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {TIERS.map(({ tier, label }) => {
                const m = byTier.get(tier);
                return (
                  <tr key={tier} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2 font-medium text-indigo-deep">{label}</td>
                    {m ? (
                      <>
                        <td className="px-3 py-2"><code className="text-xs">{m.paddleProductId}</code></td>
                        <td className="px-3 py-2"><code className="text-xs">{m.paddlePriceId}</code></td>
                        <td className="px-3 py-2">
                          {m.paddleAnnualPriceId ? <code className="text-xs">{m.paddleAnnualPriceId}</code> : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                          {new Date(m.updatedAt).toISOString().replace('T', ' ').slice(0, 16)}
                        </td>
                      </>
                    ) : (
                      <td className="px-3 py-2 text-gray-400" colSpan={4}>
                        Not configured
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setEditing(tier)}
                          className="rounded bg-indigo-primary px-3 py-1 text-xs font-medium text-white hover:opacity-90"
                        >
                          {m ? 'Edit' : 'Configure'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <MappingEditor
          tier={editing}
          label={TIERS.find((t) => t.tier === editing)?.label ?? editing}
          current={byTier.get(editing) ?? null}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function SlotResult({ v }: { v: SlotValidation }): JSX.Element {
  const slotLabel = v.slot === 'monthly' ? 'Monthly' : 'Annual';
  if (v.ok && v.price) {
    return (
      <div className="rounded border border-emerald/40 bg-emerald/5 px-3 py-2 text-sm">
        <span className="font-medium text-emerald">✓ {slotLabel}</span>{' '}
        <span className="text-gray-700">
          {formatAmount(v.price)} · {v.price.productName ?? v.price.productId}
        </span>
      </div>
    );
  }
  return (
    <div className="rounded border border-red-600/40 bg-red-600/5 px-3 py-2 text-sm" role="alert">
      <span className="font-medium text-red-600">✗ {slotLabel}</span>{' '}
      <span className="text-gray-700">{v.message}</span>
      {v.price ? (
        <div className="mt-1 text-xs text-gray-500">
          This id is actually {formatAmount(v.price)} · {v.price.productName ?? v.price.productId}
        </div>
      ) : null}
    </div>
  );
}

function MappingEditor({
  tier,
  label,
  current,
  onClose,
}: {
  tier: SubscriptionTier;
  label: string;
  current: PaddleMapping | null;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState(current?.paddleProductId ?? '');
  const [priceId, setPriceId] = useState(current?.paddlePriceId ?? '');
  const [annualPriceId, setAnnualPriceId] = useState(current?.paddleAnnualPriceId ?? '');
  const [reason, setReason] = useState('');
  // The "operator has seen the price" gate: Save is impossible until a fresh
  // validation of the CURRENT ids returns ok. Any edit clears it (re-validate).
  const [validated, setValidated] = useState<{ validations: SlotValidation[]; ok: boolean } | null>(null);

  const close = useCallback(() => onClose(), [onClose]);

  const onField = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setValidated(null); // any change invalidates the last preview
  };

  const input = (val: string) => ({
    paddleProductId: productId.trim(),
    paddlePriceId: priceId.trim(),
    paddleAnnualPriceId: val.trim() ? val.trim() : null,
  });

  const validateMut = useMutation({
    mutationFn: () => validatePaddleMapping(tier, input(annualPriceId)),
    onSuccess: (res) => setValidated(res),
  });

  const saveMut = useMutation({
    mutationFn: () => upsertPaddleMapping(tier, { ...input(annualPriceId), reason: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'paddle-mappings'] });
      close();
    },
  });

  const canValidate = productId.trim() && priceId.trim() && !validateMut.isPending;
  const canSave = validated?.ok === true && reason.trim() && !saveMut.isPending;

  const validateErr =
    validateMut.error instanceof PlatformApiError ? validateMut.error.message : validateMut.error instanceof Error ? validateMut.error.message : null;
  const saveErr =
    saveMut.error instanceof PlatformApiError ? saveMut.error.message : saveMut.error instanceof Error ? saveMut.error.message : null;

  return (
    <PlatformModal
      open
      onClose={close}
      title={`${label} — Paddle mapping`}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSave}
            onClick={() => saveMut.mutate()}
            className="rounded bg-indigo-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
            title={validated?.ok ? undefined : 'Validate the price ids against Paddle first'}
          >
            {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <Field label="Paddle product ID" value={productId} onChange={onField(setProductId)} placeholder="pro_…" />
        <Field label="Monthly price ID" value={priceId} onChange={onField(setPriceId)} placeholder="pri_…" />
        <Field
          label="Annual price ID (optional)"
          value={annualPriceId}
          onChange={onField(setAnnualPriceId)}
          placeholder="pri_… (leave blank for none)"
        />

        <div className="pt-1">
          <button
            type="button"
            disabled={!canValidate}
            onClick={() => validateMut.mutate()}
            className="rounded border border-indigo-deep px-3 py-1.5 text-sm text-indigo-deep hover:bg-indigo-deep hover:text-white disabled:opacity-40"
          >
            {validateMut.isPending ? 'Checking Paddle…' : 'Validate against Paddle'}
          </button>
        </div>

        {validateErr ? (
          <p className="text-sm text-red-600" role="alert">
            {validateErr}
          </p>
        ) : null}

        {validated ? (
          <div className="space-y-2">
            {validated.validations.map((v) => (
              <SlotResult key={v.slot} v={v} />
            ))}
            {validated.ok ? (
              <p className="text-xs text-gray-500">
                Confirm the product name and amount above match the {label} tier, then add a reason and Save.
              </p>
            ) : (
              <p className="text-xs text-red-600">Fix the blocked price id(s) — this mapping cannot be saved as-is.</p>
            )}
          </div>
        ) : null}

        <div className="pt-1">
          <label className="mb-1 block text-xs font-medium text-gray-600">Reason (audited)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. wiring the Starter catalogue"
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-indigo-primary focus:outline-none"
          />
        </div>

        {saveErr ? (
          <p className="text-sm text-red-600" role="alert">
            {saveErr}
          </p>
        ) : null}
      </div>
    </PlatformModal>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded border border-gray-300 px-2 py-1.5 font-mono text-sm focus:border-indigo-primary focus:outline-none"
      />
    </div>
  );
}

export const platformPaddleMappingsRoute = createRoute({
  getParentRoute: () => platformLayoutRoute,
  path: '/platform/paddle-mappings',
  component: PaddleMappingsPage,
});
